import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { generateClient } from "aws-amplify/api";
import { FiAlertTriangle } from "react-icons/fi";
import { ErrorLogService } from "../../services/errorLogService";
import { ProcessedModelRunHistoryService } from "../../services/processedModelRunHistoryService";
import {
  onCreateErrorLog,
  onUpdateErrorLog,
} from "../../graphql/subscriptions";
import {
  playErrorLogChime,
  playFailedRunChime,
} from "../../utils/notificationChime";
import { SubscriptionObservable } from "../../types/graphqlSubscription";

const client = generateClient();

// Inline subscriptions for processed-model run history — select the minimal fields the badge needs (status to detect failures, acknowledged so an acknowledge update recomputes the badge), independent of codegen regeneration timing. See PROCESSED_MODEL_RUN_TRIAGE.md.
const ON_CREATE_RUN = `
  subscription OnCreateProcessedModelRunHistory {
    onCreateProcessedModelRunHistory { id status }
  }
`;
const ON_UPDATE_RUN = `
  subscription OnUpdateProcessedModelRunHistory {
    onUpdateProcessedModelRunHistory { id acknowledged }
  }
`;

interface OnCreateRunData {
  onCreateProcessedModelRunHistory?: { status?: string | null } | null;
}
interface OnUpdateRunData {
  onUpdateProcessedModelRunHistory?: { id: string } | null;
}

interface OnCreateErrorLogData {
  onCreateErrorLog?: { status?: string | null } | null;
}

interface OnUpdateErrorLogData {
  onUpdateErrorLog?: { id: string; status?: string | null } | null;
}

interface AdminErrorLogBellProps {
  /** Show the text label (nav expanded state). */
  showLabel?: boolean;
}

const AdminErrorLogBell: React.FC<AdminErrorLogBellProps> = ({
  showLabel = false,
}) => {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  // Unacknowledged FAILED processed-model runs (blue badge, bottom-right of the same icon). Admins clear runs via the Acknowledge button, which decrements this count (see PROCESSED_MODEL_RUN_TRIAGE.md).
  const [failedCount, setFailedCount] = useState(0);

  const refreshCount = useCallback(async () => {
    const open = await ErrorLogService.countOpenErrorLogs();
    setCount(open);
  }, []);

  const refreshFailedCount = useCallback(async () => {
    const failed =
      await ProcessedModelRunHistoryService.countUnacknowledgedFailed();
    setFailedCount(failed);
  }, []);

  // Seed the open count once on mount. Live create/update subscriptions below keep it in sync afterwards, so we deliberately do NOT recount on every route change — countOpenErrorLogs paginates a filtered scan and that added up to many AppSync calls per navigation.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const open = await ErrorLogService.countOpenErrorLogs();
      if (!cancelled) setCount(open);
      const failed =
        await ProcessedModelRunHistoryService.countUnacknowledgedFailed();
      if (!cancelled) setFailedCount(failed);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Live CREATE subscription: a newly created error log is always OPEN — bump + chime.
  useEffect(() => {
    const observable = client.graphql({
      query: onCreateErrorLog,
    }) as unknown as SubscriptionObservable<OnCreateErrorLogData>;

    const sub = observable.subscribe({
      next: ({ data }) => {
        const newLog = data?.onCreateErrorLog;
        if (!newLog) return;
        if (!newLog.status || newLog.status === "OPEN") {
          setCount((c) => c + 1);
          playErrorLogChime();
        }
      },
      error: (err) => {
        console.error("ErrorLog create subscription error:", err);
      },
    });
    return () => {
      try {
        sub.unsubscribe();
      } catch {
        /* noop */
      }
    };
  }, []);

  // Live UPDATE subscription: a status change by any admin can move a log in/out of OPEN. Recompute the count from the server so every admin's badge stays in sync (e.g. goes down).
  useEffect(() => {
    const observable = client.graphql({
      query: onUpdateErrorLog,
    }) as unknown as SubscriptionObservable<OnUpdateErrorLogData>;

    const sub = observable.subscribe({
      next: ({ data }) => {
        if (!data?.onUpdateErrorLog) return;
        refreshCount();
      },
      error: (err) => {
        console.error("ErrorLog update subscription error:", err);
      },
    });
    return () => {
      try {
        sub.unsubscribe();
      } catch {
        /* noop */
      }
    };
  }, [refreshCount]);

  // Live CREATE: a new FAILED processed-model run bumps the blue badge + plays a distinct chime.
  useEffect(() => {
    const observable = client.graphql({
      query: ON_CREATE_RUN,
    }) as unknown as SubscriptionObservable<OnCreateRunData>;

    const sub = observable.subscribe({
      next: ({ data }) => {
        const run = data?.onCreateProcessedModelRunHistory;
        if (!run) return;
        if (run.status === "FAILED") {
          setFailedCount((c) => c + 1);
          playFailedRunChime();
        }
      },
      error: (err) => {
        console.error("Run create subscription error:", err);
      },
    });
    return () => {
      try {
        sub.unsubscribe();
      } catch {
        /* noop */
      }
    };
  }, []);

  // Live UPDATE: a status change (e.g. PROCESSING -> FAILED) can change the failed count. Recompute from the server so every admin's blue badge stays in sync.
  useEffect(() => {
    const observable = client.graphql({
      query: ON_UPDATE_RUN,
    }) as unknown as SubscriptionObservable<OnUpdateRunData>;

    const sub = observable.subscribe({
      next: ({ data }) => {
        if (!data?.onUpdateProcessedModelRunHistory) return;
        refreshFailedCount();
      },
      error: (err) => {
        console.error("Run update subscription error:", err);
      },
    });
    return () => {
      try {
        sub.unsubscribe();
      } catch {
        /* noop */
      }
    };
  }, [refreshFailedCount]);

  const label = count > 0 ? `Error Logs (${count})` : "Error Logs";

  return (
    <button
      onClick={() => navigate("/home/admin/error-logs")}
      title={label}
      aria-label={label}
      className="group eai-nav-item"
    >
      <div className="eai-nav-icon relative group-hover:text-eai-teal-deep">
        <FiAlertTriangle className="h-4 w-4" />
        {/* Red badge (top-right): open reported error logs */}
        {count > 0 && (
          <span className="absolute -top-2 -right-2 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-red-600 text-[10px] font-bold leading-none text-white">
            {count > 99 ? "99+" : count}
          </span>
        )}
        {/* Blue badge (bottom-right): failed processed-model runs */}
        {failedCount > 0 && (
          <span className="absolute -bottom-2 -right-2 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold leading-none text-white">
            {failedCount > 99 ? "99+" : failedCount}
          </span>
        )}
      </div>
      <span className={`eai-nav-label ${showLabel ? "opacity-100" : "opacity-0"}`}>
        Error Logs
      </span>
    </button>
  );
};

export default AdminErrorLogBell;
